import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, TouchableOpacity, type ViewInstance } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { Icon } from '@garnish/core';
import { BodyText } from '@garnish/core';

import { NAVBAR_MENU_ANIMATION_DURATION } from '../../../../Navbar.constants';
import { focusNavbarMenuElement } from '../../NavbarMenu.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarMenuListItem = (props: Props) => {
  const { autoFocus = false, children, isLiquidGlassEnabled = false, onPress } = props;

  const isReduceMotionEnabled = useReducedMotion();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const containerRef = useRef<ViewInstance>(null);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const focusItem = useCallback(() => {
    focusNavbarMenuElement(containerRef.current);
  }, []);

  // NOTE: The menu enters hidden on web and fully transparent on native, where
  //       nothing can take focus, so focus waits for the entrance to finish.
  const focusOnMount = useCallback(() => {
    if (!autoFocus) {
      return;
    }

    const focusDelay = isReduceMotionEnabled ? 0 : NAVBAR_MENU_ANIMATION_DURATION;
    const focusTimeout = setTimeout(focusItem, focusDelay);

    return () => {
      clearTimeout(focusTimeout);
    };
  }, [autoFocus, focusItem, isReduceMotionEnabled]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(focusOnMount, [focusOnMount]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  // NOTE: Glass lets more of the content behind through, so the caret needs
  //       more contrast to stay visible.
  const iconColor = isLiquidGlassEnabled ? theme.colors.OPACITY.BLACK.DARK : theme.colors.GRAY;

  return (
    <TouchableOpacity
      ref={containerRef}
      accessibilityLabel={children}
      accessibilityRole="button"
      activeOpacity={0.7}
      style={styles.container}
      onPress={onPress}
    >
      <BodyText sizeMatch={['14']} style={styles.label}>
        {children}
      </BodyText>

      <Icon name="IconCaretRight" color={iconColor} width={ICON_SIZE} height={ICON_SIZE} />
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZE = 20;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['2'],
    paddingVertical: theme.spacing['4'],
  },
  label: {
    flexShrink: 1,
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  autoFocus?: boolean;
  children: string;
  isLiquidGlassEnabled?: boolean;
  onPress: () => void;
};
